import { useTranslation } from 'react-i18next';

import { formatHoursToTimeString } from '@/helpers';

import { TimeOffStatusSwatch } from '../../time-off/time-off-status-swatch';
import { TimeOffTotals } from './timeoff-strip.helpers';

interface TimeOffWeeklyTotalCellProps {
  totals: TimeOffTotals;
}

/**
 * Right-most cell of the time-off row: the week's approved and requested hour
 * totals, each on its own left-aligned line with a status swatch. Lines are
 * omitted when their total is zero; the font shrinks on small/medium screens.
 */
export function TimeOffWeeklyTotalCell({ totals }: TimeOffWeeklyTotalCellProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  return (
    <div className="flex flex-col items-start justify-center gap-1 border-b border-l bg-muted/40 px-3 py-3">
      {totals.approved > 0 && (
        <TotalLine
          isApproved
          label={t('weekView.timeOff.weeklyApproved', {
            hours: formatHoursToTimeString(totals.approved),
          })}
        />
      )}
      {totals.requested > 0 && (
        <TotalLine
          isApproved={false}
          label={t('weekView.timeOff.weeklyRequested', {
            hours: formatHoursToTimeString(totals.requested),
          })}
        />
      )}
    </div>
  );
}

/**
 * One total line: a status swatch + label. `items-start` keeps the swatch
 * aligned to the first text line when the label wraps on narrow columns.
 */
function TotalLine({ label, isApproved }: { label: string; isApproved: boolean }) {
  return (
    <div className="flex items-start gap-1.5 leading-tight">
      <TimeOffStatusSwatch isApproved={isApproved} className="mt-[3px]" />
      <span className="text-[10px] font-semibold tabular-nums text-time-off-text md:text-[10.5px] lg:text-[11.5px]">
        {label}
      </span>
    </div>
  );
}
